import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { useCourse } from '../context/CourseContext';
import { getResumeLessonId } from '../services/courseService';
import { isEnrollmentExpired } from '../services/enrollmentService';
import { useSchoolPasses } from '../hooks/useSchoolPasses';
import { coveringPass } from '../services/schoolPassService';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { formatPrice } from '../lib/format';
import { publicService, type PublicCourseDetail } from '../services/publicService';
import { UserRole, LessonType, type Course, type Lesson } from '../types';
import {
  CheckCircle,
  PlayCircle,
  Award,
  Lock,
  Coins,
  FileText,
  Pencil,
  HelpCircle,
  Flag,
  ArrowRight,
  Star,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { springs } from '../components/ui/slide-animations';
import { AnimatedSection } from '../components/ui/AnimatedSection';
import { Skeleton, SkeletonText } from '../components/ui/Skeleton';
import { PageContainer } from '../components/ui/PageContainer';
import { Breadcrumbs, type Crumb } from '../components/ui/Breadcrumbs';
import { lessonIcon, countSlideTemplates } from './courseDetail/lessonIcon';
import { STUDENT_COUNT_THRESHOLD } from '../lib/courseMeta';
import { useTagCategories } from '../hooks/useTagCategories';
import { resolveGroupingConfig } from '../lib/catalogGrouping';
import { heroContext } from '../components/home/GroupedHomeSections';
import { thumbnailSrc } from '../lib/imageUrl';
import { onThumbError } from '../components/ui/imageFallback';

type ViewState = 'prospect' | 'not-started' | 'in-progress' | 'complete';

const VISIBLE_TAG_LIMIT = 3;

const isUntitledHeading = (title?: string | null) => {
  const trimmed = (title ?? '').trim().toLowerCase();
  return trimmed === '' || trimmed === 'new heading' || trimmed === 'untitled';
};

const lessonTypeFromString = (raw: string): LessonType => {
  switch ((raw || '').toUpperCase()) {
    case 'VIDEO': return LessonType.VIDEO;
    case 'QUIZ': return LessonType.QUIZ;
    case 'ASSIGNMENT': return LessonType.ASSIGNMENT;
    case 'HEADING': return LessonType.HEADING;
    default: return LessonType.TEXT;
  }
};

/** Adapt a publicService PublicCourseDetail into the Course shape the page renders.
 *  Fields publicService doesn't expose (full lesson content, certificateEnabled,
 *  points, instructorId) are stubbed; the UI null-checks them where shown. */
const adaptPublicCourse = (pc: PublicCourseDetail): Course => ({
  id: pc.id,
  instructorId: '',
  title: pc.title,
  description: pc.description,
  thumbnailUrl: pc.thumbnailUrl,
  instructorName: pc.instructorName,
  price: pc.price,
  category: pc.category,
  level: pc.level,
  totalStudents: pc.totalStudents,
  rating: pc.rating,
  points: 100, // matches the existing UI default for courses that don't set this
  published: true,
  certificateEnabled: false,
  showFacilitator: pc.showFacilitator ?? false,
  tags: pc.tags,
  contentLoaded: false,
  sections: pc.sections.map(section => ({
    id: section.id,
    courseId: pc.id,
    title: section.title,
    order: section.order,
    lessons: section.lessons.map(lesson => ({
      id: lesson.id,
      sectionId: section.id,
      title: lesson.title,
      type: lessonTypeFromString(lesson.type),
      duration: lesson.duration,
      content: '',
      isCompleted: false,
      isLocked: !lesson.isPreview,
      order: lesson.order,
    })),
  })),
});

export const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { courses, enrollInCourse, getEnrollment, isLessonCompleted, isLoading: courseCtxLoading } = useCourse();
  const { user } = useAuth();
  const { tenant, features } = useTenant();

  // `sell_courses` is authoritative: when off, this tenant cannot charge, so
  // every course is free-enrol regardless of any (possibly stale) stored price.
  const canSell = features.sell_courses;

  // Points only exist where gamification does. A tenant with it off has no
  // points anywhere else in the shell, so a "100 pts on completion" promise in
  // the hero is a reward the learner will never be given.
  const showPoints = features.gamification;

  // Mirrors the route table in App.tsx: `/courses` only renders the public
  // catalogue when BOTH hold. Otherwise it redirects to login (unsold tenant)
  // or shows ComingSoon (unpublished), so it is not a breadcrumb ancestor.
  const marketingCatalogLive = canSell && !!tenant?.isPublished;

  const isInstructor = user?.role === UserRole.INSTRUCTOR || user?.role === UserRole.ADMIN;

  // Grouped tenants: the hero's eyebrow becomes the course's real taxonomy
  // ("Practice Lab · Paper 1 · Q1") and the title trims to the topic — the
  // pipe-string prefix stops dictating the headline's size and wrap.
  const groupingConfigured = !!tenant?.branding?.catalog?.groupBy;
  const { categories: tagCategories } = useTagCategories(tenant?.id ?? null, groupingConfigured);
  const grouping = React.useMemo(
    () => resolveGroupingConfig(tenant?.branding?.catalog, tagCategories),
    [tenant?.branding?.catalog, tagCategories],
  );

  // Authed users get their course from CourseContext (already loaded). Anonymous
  // users — and authed users on a course not in their context (rare) — fall back
  // to publicService.getPublicCourseById for a curriculum outline.
  const ctxCourse = courses.find(c => c.id === id);
  const [publicCourse, setPublicCourse] = useState<Course | null>(null);
  const [publicLoading, setPublicLoading] = useState(false);
  const [publicNotFound, setPublicNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPublicCourse(null);
    setPublicNotFound(false);

    // Only the public fallback fetch below should hold publicLoading. On every
    // other path — auth already has the course, auth still settling, or no id —
    // clear it. Otherwise a fetch kicked off on one render gets orphaned when a
    // re-run (e.g. courseCtxLoading flipping as auth/tenant settle) early-returns
    // here, and its .finally is suppressed by `cancelled`, leaving publicLoading
    // stuck true and the page on a permanent loading skeleton.
    if (ctxCourse || courseCtxLoading || !id) {
      setPublicLoading(false);
      return;
    }

    setPublicLoading(true);
    publicService.getPublicCourseById(id)
      .then(result => {
        if (cancelled) return;
        if (!result) setPublicNotFound(true);
        else setPublicCourse(adaptPublicCourse(result));
      })
      .catch(err => {
        if (cancelled) return;
        console.warn('Public course fetch failed:', err);
        setPublicNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setPublicLoading(false);
      });

    return () => { cancelled = true; };
  }, [id, ctxCourse, courseCtxLoading]);

  const course = ctxCourse ?? publicCourse;
  const isLoading = courseCtxLoading || publicLoading;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const enrollment = course ? getEnrollment(course.id) : undefined;
  // School pass: a read-time entitlement — while active, a covered course
  // opens without payment and an expired bundle enrollment stays continuable.
  // Empty for tenants without the flag (the hook fetches nothing there).
  const { passes: schoolPasses, hidePrices } = useSchoolPasses();
  const schoolPass = course ? coveringPass(schoolPasses, course.id) : undefined;
  // Bundle-granted access can lapse; an expired enrollment keeps its
  // progress but can't be continued until renewed (re-purchase the bundle).
  const accessExpired = !!enrollment && isEnrollmentExpired(enrollment) && !schoolPass;
  const isEnrolled = !!enrollment && !accessExpired;

  const [enrolling, setEnrolling] = useState(false);
  const [tagsExpanded, setTagsExpanded] = useState(false);

  // Sticky action bar: mirrors the hero CTA once it scrolls out of view, so
  // the sidebar's old job (a CTA that's always reachable) survives the
  // sidebar's removal. jsdom has no IntersectionObserver — the guard leaves
  // the bar off there, which is also the safe degradation in any browser
  // without it.
  const [ctaInView, setCtaInView] = useState(true);
  const ctaSentinelRef = React.useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const el = ctaSentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setCtaInView(entry.isIntersecting),
      { rootMargin: '-72px 0px 0px 0px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [course?.id]);

  if (isLoading) {
    return (
      <div className="bg-white min-h-screen">
        <div className="bg-[#151926] pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl space-y-4">
              <Skeleton className="h-4 w-56 !bg-white/10" />
              <div className="flex gap-3">
                <Skeleton className="h-6 w-24 rounded-full !bg-white/10" />
                <Skeleton className="h-6 w-20 rounded-full !bg-white/10" />
              </div>
              <Skeleton className="h-12 w-3/4 !bg-white/10" />
              <Skeleton className="h-6 w-full !bg-white/10" />
              <Skeleton className="h-6 w-2/3 !bg-white/10" />
            </div>
          </div>
        </div>
        <PageContainer>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8 space-y-4">
              <Skeleton className="h-8 w-48" />
              {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
            <div className="lg:col-span-4">
              <Skeleton className="h-52 w-full rounded-xl" />
              <div className="mt-6 space-y-3">
                <Skeleton className="h-10 w-full rounded-lg" />
                <SkeletonText lines={3} />
              </div>
            </div>
          </div>
        </PageContainer>
      </div>
    );
  }

  if (!course) {
    if (!publicNotFound) {
      // Both sources still settling — let the loading skeleton above handle it.
      return null;
    }
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Course not found</h2>
          <p className="text-gray-500 mb-6">This course may have been removed or the link is incorrect.</p>
          <Link to={user ? '/browse' : '/courses'} className="text-primary-600 hover:text-primary-700 font-semibold">
            Browse Courses
          </Link>
        </div>
      </div>
    );
  }

  // ----- derived state -----

  const allItems: Lesson[] = course.sections.flatMap(s => s.lessons);
  const contentLessons = allItems.filter(l => l.type !== LessonType.HEADING);
  const totalLessons = contentLessons.length;
  // No course length is shown. `lessons.duration` is an estimate an author
  // rarely revisits, so the totals it produced were consistently wrong — a
  // confidently precise "~45 min" that nothing kept true. Lesson COUNT is
  // derived from real rows, so that is what the student sees instead.
  const sectionCount = course.sections.length;

  const completedIds = enrollment?.completedLessonIds ?? [];
  const completedSet = new Set(completedIds);
  const completedCount = contentLessons.filter(l => completedSet.has(l.id)).length;
  const progress = enrollment?.progress ?? 0;

  const viewState: ViewState = !isEnrolled
    ? 'prospect'
    : progress >= 100
      ? 'complete'
      : progress > 0 || completedCount > 0
        ? 'in-progress'
        : 'not-started';

  const resumeLessonId = getResumeLessonId(course, completedIds);
  const resumeLesson = contentLessons.find(l => l.id === resumeLessonId);

  // Both of these inspect slide templates, so they only produce a result when the
  // course copy actually carries lesson content. Students (getAllCourses) and the
  // public fetch behave as before; instructors/admins get a metadata-only course
  // from getInstructorCourses (`contentLoaded: false`), so for them the chips stay
  // hidden and lessonIcon falls back to the lesson-type icon — the same graceful
  // degradation anonymous visitors have always had on the public path, and not
  // worth refetching the whole slide payload to restore.
  const { quizCount, reflectionCount, mediaCount } = countSlideTemplates(contentLessons);

  // Grouped identity: eyebrow = taxonomy context, title = trimmed topic. Null
  // for non-grouped tenants (or an untagged course) — everything falls back to
  // the classic category/level eyebrow and full title.
  const groupLabel = grouping ? heroContext(course, grouping) : null;
  const heroTitle = groupLabel?.title ?? course.title;

  const visibleTags = course.tags ?? [];
  const tagsToShow = tagsExpanded ? visibleTags : visibleTags.slice(0, VISIBLE_TAG_LIMIT);
  const hiddenTagCount = Math.max(0, visibleTags.length - VISIBLE_TAG_LIMIT);

  // ----- breadcrumbs -----
  //
  // This one component serves three audiences on two routes (`/enroll/:id`
  // inside the app shell, `/course/:id` as a public preview), so the trail is
  // derived from who is looking rather than from the URL — each gets the
  // ancestors their own nav actually offers them. Anonymous visitors on a
  // tenant with no public catalogue get no trail at all; Breadcrumbs drops a
  // lone crumb, so nothing needs to special-case that here.
  const breadcrumbs: Crumb[] = isInstructor
    ? [
        { label: 'Instructor', to: '/instructor' },
        { label: 'Courses', to: '/instructor/courses' },
        { label: heroTitle },
      ]
    : user
      ? [
          { label: 'Home', to: '/browse' },
          { label: 'Courses', to: '/catalog' },
          { label: heroTitle },
        ]
      : marketingCatalogLive
        ? [{ label: 'Courses', to: '/courses' }, { label: heroTitle }]
        : [{ label: heroTitle }];

  // The hero's fact row is entirely conditional now that points can be off, so
  // an unrated, unenrolled, no-certificate course on a non-points tenant would
  // otherwise render an empty flex row and its gap.
  const hasHeroMeta =
    showPoints ||
    course.rating > 0 ||
    course.totalStudents >= STUDENT_COUNT_THRESHOLD ||
    !!course.certificateEnabled;

  // ----- handlers -----

  const handleAction = async () => {
    // Anonymous → bounce through login, returning to the public preview URL.
    if (!user) {
      navigate(`/login?redirect=/course/${course.id}`);
      return;
    }
    if (isEnrolled) {
      const targetLessonId = resumeLessonId;
      if (targetLessonId) {
        navigate(`/learn/${course.id}/lesson/${targetLessonId}`);
      }
      return;
    }
    // Paid courses go through checkout; free courses enrol in one click.
    // Only route to checkout when the tenant can actually sell — otherwise a
    // stale price would send the student to an unmounted /checkout route (404).
    // A school pass makes a paid course free-enrol (the enroll_in_course RPC
    // verifies coverage server-side; the client check only picks the path).
    if (canSell && course.price > 0 && !schoolPass) {
      navigate(`/checkout/${course.id}`);
      return;
    }
    setEnrolling(true);
    try {
      await enrollInCourse(course.id);
      const firstContentLesson = course.sections
        .flatMap(s => [...s.lessons].sort((a, b) => a.order - b.order))
        .find(l => l.type !== LessonType.HEADING);
      if (firstContentLesson) {
        navigate(`/learn/${course.id}/lesson/${firstContentLesson.id}`);
      }
    } catch (err) {
      console.warn('Enroll error:', err);
      setEnrolling(false);
    }
  };

  const handleReview = () => {
    const first = contentLessons[0];
    if (first) navigate(`/learn/${course.id}/lesson/${first.id}`);
  };

  // ----- hero CTA block (state-aware) -----
  //
  // With the sidebar gone ("the jacket" layout), the hero carries the primary
  // action for EVERY state — the sticky bottom bar mirrors it once this
  // scrolls out of view.

  const renderHeroCta = () => {
    if (isInstructor) {
      return (
        <motion.div
          className="mt-8"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.55 }}
        >
          <Button
            onClick={() => navigate(`/instructor/edit/${course.id}`)}
            size="lg"
            className="h-12 px-6 font-bold shadow-lg shadow-black/20"
          >
            <Pencil className="h-4 w-4 mr-2" /> Edit Course
          </Button>
        </motion.div>
      );
    }

    if (accessExpired) {
      return (
        <motion.div
          className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.55 }}
        >
          <Button
            onClick={() =>
              features.bundles && enrollment?.sourceBundleId
                ? navigate(`/bundle/${enrollment.sourceBundleId}`)
                : handleAction()
            }
            size="lg"
            className="h-12 px-6 font-bold shadow-lg shadow-black/20"
          >
            Renew access
          </Button>
          <span className="text-sm text-amber-200/90">
            Your access expired — renew to keep your progress and continue.
          </span>
        </motion.div>
      );
    }

    if (viewState === 'prospect') {
      return (
        <motion.div
          className="mt-8 flex flex-col sm:flex-row sm:items-center gap-x-5 gap-y-3"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.55 }}
        >
          <Button
            onClick={handleAction}
            size="lg"
            className="h-12 px-6 font-bold shadow-lg shadow-black/20"
            disabled={enrolling}
          >
            {enrolling
              ? 'Enrolling…'
              : !user
                ? 'Sign in to enrol'
                : !canSell || schoolPass
                  ? 'Start course'
                  : course.price > 0
                    ? 'Enrol now'
                    : 'Get free access'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          {/* The decision and its inputs on one line — price, then what the
              money buys. This row absorbs the old sidebar's includes card. */}
          <span className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-sm text-white/65">
            {canSell && schoolPass ? (
              <span className="font-serif text-2xl leading-none text-white">
                Covered by {schoolPass.schoolName}
              </span>
            ) : canSell && !(hidePrices && course.price > 0) ? (
              <span className="font-serif text-2xl leading-none text-white">
                {course.price > 0 ? formatPrice(course.price) : 'Free'}
              </span>
            ) : null}
            <span>
              {totalLessons} lesson{totalLessons === 1 ? '' : 's'}
            </span>
            {mediaCount > 0 && <span>· {mediaCount} interactive</span>}
            {quizCount > 0 && (
              <span>
                · {quizCount} quiz{quizCount === 1 ? '' : 'zes'}
              </span>
            )}
            {course.certificateEnabled && <span>· Certificate</span>}
          </span>
        </motion.div>
      );
    }

    if (viewState === 'not-started') {
      return (
        <motion.div
          className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.55 }}
        >
          <Button
            onClick={handleAction}
            size="lg"
            className="h-12 px-6 font-bold shadow-lg shadow-black/20"
          >
            Start Learning <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <span className="text-sm text-primary-200/90">
            {totalLessons} lesson{totalLessons === 1 ? '' : 's'}
          </span>
        </motion.div>
      );
    }

    if (viewState === 'in-progress') {
      return (
        <motion.div
          className="mt-8 max-w-xl"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.55 }}
        >
          <div className="flex items-center justify-between mb-2 text-xs uppercase tracking-wider text-primary-200/80 font-semibold">
            <span>{completedCount} of {totalLessons} complete</span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 rounded-full bg-primary-800 overflow-hidden">
            <motion.div
              className="h-full bg-primary-400 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <Button
              onClick={handleAction}
              size="lg"
              className="h-12 px-6 font-bold shadow-lg shadow-black/20"
            >
              Resume <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            {resumeLesson && (
              <span className="text-sm text-primary-100 truncate">
                Next: <span className="font-semibold">{resumeLesson.title}</span>
              </span>
            )}
          </div>
        </motion.div>
      );
    }

    // complete
    return (
      <motion.div
        className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.55 }}
      >
        <div className="inline-flex items-center gap-2 rounded-full bg-amber-400/15 ring-1 ring-amber-300/40 px-4 py-2 text-amber-200 text-sm font-semibold">
          <Flag className="h-4 w-4" />
          Course complete
        </div>
        {course.certificateEnabled && (
          <Link
            to={`/certificate/${course.id}`}
            className="inline-flex items-center gap-2 text-primary-100 hover:text-white text-sm font-semibold underline-offset-4 hover:underline"
          >
            <Award className="h-4 w-4" /> View certificate
          </Link>
        )}
        <button
          type="button"
          onClick={handleReview}
          className="text-sm text-primary-200/90 hover:text-white font-semibold"
        >
          Review course →
        </button>
      </motion.div>
    );
  };

  return (
    <div className="bg-paper min-h-screen">
      {/* Ink hero — a neutral deep ground the brand accents sit on, instead of
          the old wall of primary-900 that swallowed every tenant's palette.
          The subtle radial tint keeps it from reading flat. */}
      {/* overflow stays visible so the art card can hang over the bottom edge */}
      <div className="relative border-b border-white/5 bg-[#151926] pt-20 pb-12 text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-primary-500/[0.07]"
          style={{ maskImage: 'radial-gradient(ellipse 80% 90% at 20% 0%, black, transparent 70%)' }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* "The jacket": text and art composed against each other, the art
              hanging over the hero's bottom edge into the body — the overlap
              is what stitches the two zones into one page. */}
          <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="max-w-3xl lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Breadcrumbs items={breadcrumbs} tone="dark" className="mb-6" />
            </motion.div>

            {/* Eyebrow: the course's real taxonomy on grouped tenants; the
                classic category · level pair everywhere else. */}
            <motion.div
              className="mb-4 text-[11px] font-bold uppercase tracking-[0.2em]"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 }}
            >
              {groupLabel ? (
                <span className="text-primary-300">{groupLabel.context}</span>
              ) : (
                <span className="flex items-center gap-3">
                  <span className="text-primary-300">{course.category}</span>
                  <span className="text-white/25" aria-hidden>·</span>
                  <span className="text-white/50">{course.level}</span>
                </span>
              )}
            </motion.div>

            <motion.h1
              className="relative mb-5 inline-block text-3xl font-bold leading-[1.15] md:text-5xl"
              style={{ textWrap: 'balance' as never }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...(springs.gentle as object), delay: 0.1 }}
            >
              {heroTitle}
              <motion.span
                className="absolute left-0 -bottom-2 h-[3px] bg-primary-400 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: '38%' }}
                transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              />
            </motion.h1>

            <motion.p
              className="mb-8 max-w-2xl text-lg font-light leading-relaxed text-white/75"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.25 }}
            >
              {course.description}
            </motion.p>

            {/* Facilitator credit is per-course opt-in (default off): on a
                single-facilitator tenant the row was dead metadata on every
                course. Toggle lives in the builder's settings panel. */}
            {course.showFacilitator && course.instructorName && (
              <motion.div
                className="flex items-center gap-3 mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="h-10 w-10 rounded-full bg-primary-700 ring-1 ring-white/20 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {course.instructorName.charAt(0).toUpperCase()}
                </div>
                <div className="text-sm leading-tight">
                  <div className="text-white font-semibold">{course.instructorName}</div>
                  <div className="text-primary-200/70 text-xs">Facilitator</div>
                </div>
              </motion.div>
            )}

            {hasHeroMeta && (
            <motion.div
              className="flex items-center gap-x-8 gap-y-3 flex-wrap text-sm text-primary-200"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.35 }}
            >
              {showPoints && (
                <div className="flex items-baseline gap-2 text-amber-400">
                  <Coins className="h-5 w-5 self-center" />
                  <span className="font-bold text-2xl leading-none">{course.points ?? 100}</span>
                  <span className="font-semibold text-sm">pts</span>
                  <span className="ml-1 opacity-75 text-primary-200 text-xs">on completion</span>
                </div>
              )}
              {course.rating > 0 && (
                <div className="flex items-center gap-1.5">
                  <Star className="h-4 w-4 text-amber-400 fill-current" />
                  <span>{course.rating.toFixed(1)}</span>
                </div>
              )}
              {course.totalStudents >= STUDENT_COUNT_THRESHOLD && (
                <div className="flex items-center gap-2">
                  <span>{course.totalStudents.toLocaleString()} students</span>
                </div>
              )}
              {course.certificateEnabled && (
                <div className="flex items-center gap-1.5">
                  <Award className="h-4 w-4" />
                  <span>Certificate</span>
                </div>
              )}
            </motion.div>
            )}

            {/* Tag chips only when the eyebrow isn't already the taxonomy —
                on grouped tenants they'd repeat it word for word. */}
            {!groupLabel && visibleTags.length > 0 && (
              <motion.div
                className="mt-5 flex items-center flex-wrap gap-x-3 gap-y-1 text-[10px] uppercase tracking-wider"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.45 }}
              >
                {tagsToShow.map((tag, i) => (
                  <React.Fragment key={tag.id}>
                    {i > 0 && <span className="text-primary-400/60" aria-hidden>·</span>}
                    <span className="inline-flex items-center gap-1.5 text-primary-200/80 font-semibold">
                      <span
                        aria-hidden
                        className="h-1.5 w-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: tag.color || 'rgb(252 165 165 / 0.6)' }}
                      />
                      {tag.name}
                    </span>
                  </React.Fragment>
                ))}
                {!tagsExpanded && hiddenTagCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setTagsExpanded(true)}
                    className="text-primary-300/70 font-semibold hover:text-white transition-colors"
                  >
                    +{hiddenTagCount} more
                  </button>
                )}
              </motion.div>
            )}

            {renderHeroCta()}
            {/* Sentinel: when the hero CTA leaves the viewport, the sticky
                action bar takes over. */}
            <div ref={ctaSentinelRef} aria-hidden />
          </div>

          {course.thumbnailUrl && (
            <motion.div
              className="relative z-20 lg:col-span-5 lg:-mb-28"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...(springs.gentle as object), delay: 0.25 }}
            >
              <div className="aspect-video overflow-hidden rounded-2xl shadow-2xl shadow-black/40 ring-1 ring-white/10 lg:aspect-[4/3]">
                <img src={thumbnailSrc(course.thumbnailUrl, 'hero')} alt={course.title} className="h-full w-full object-cover" onError={onThumbError} />
              </div>
            </motion.div>
          )}
          </div>
        </div>

        {/* Soft transition into body */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-black/10" />
      </div>

      <PageContainer className="!py-12">
        {/* One strong column, left-aligned under the hero text — the art hangs
            into the empty right side, so the two zones interlock instead of
            stacking. The curriculum IS the body: no sidebar, no repeated
            description. */}
        <div className="max-w-3xl">
          <AnimatedSection delay={0.1}>
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Course Content</h2>
                <div className="text-sm font-semibold text-gray-500">
                  {totalLessons} lesson{totalLessons === 1 ? '' : 's'}
                </div>
              </div>

              <div className="border border-paper-line rounded-xl overflow-hidden bg-white shadow-sm">
                {(() => {
                  // Rows were inert 14px lines that treated the curriculum as
                  // decoration. Numbered and clickable (once you can open
                  // them), they become the map of the course — the thing this
                  // page exists to show.
                  const canOpen = isEnrolled || isInstructor;
                  let n = 0;
                  return allItems.map(lesson => {
                    if (lesson.type === 'HEADING') {
                      if (isUntitledHeading(lesson.title)) {
                        return <div key={lesson.id} className="border-b border-paper-line" aria-hidden />;
                      }
                      return (
                        <div key={lesson.id} className="bg-paper-2 px-6 pt-4 pb-3 border-b border-paper-line cursor-default">
                          <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-gray-500">
                            {lesson.title}
                          </span>
                        </div>
                      );
                    }
                    n += 1;
                    const completed = isLessonCompleted(course.id, lesson.id);
                    const isUpNext = viewState === 'in-progress' && lesson.id === resumeLessonId;
                    const Icon = lessonIcon(lesson);
                    const inner = (
                      <>
                        <span
                          className={`w-7 flex-shrink-0 text-right text-xs font-bold tabular-nums ${
                            completed ? 'text-green-600' : 'text-gray-300 group-hover:text-gray-500'
                          }`}
                        >
                          {String(n).padStart(2, '0')}
                        </span>
                        <Icon className="h-4 w-4 flex-shrink-0 text-gray-400 group-hover:text-primary-600" />
                        <span
                          className={`flex-1 text-left text-[15px] font-medium ${
                            completed ? 'text-gray-500' : 'text-gray-800'
                          } group-hover:text-gray-900`}
                        >
                          {lesson.title}
                        </span>
                        <span className="flex flex-shrink-0 items-center gap-3">
                          {isUpNext && (
                            <span className="rounded-full bg-primary-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-700">
                              Up next
                            </span>
                          )}
                          {completed ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : lesson.isLocked && !isEnrolled ? (
                            <Lock className="h-3.5 w-3.5 text-gray-300" />
                          ) : canOpen ? (
                            <ArrowRight className="h-4 w-4 text-gray-300 opacity-0 transition-opacity group-hover:opacity-100 group-hover:text-primary-600" />
                          ) : null}
                        </span>
                      </>
                    );
                    const rowClass =
                      'w-full px-5 py-4 flex items-center gap-3.5 transition-colors group border-b border-paper-line last:border-0' +
                      (isUpNext ? ' bg-primary-50/40' : '');
                    return canOpen ? (
                      <button
                        key={lesson.id}
                        type="button"
                        onClick={() => navigate(`/learn/${course.id}/lesson/${lesson.id}`)}
                        className={`${rowClass} hover:bg-paper-2 cursor-pointer`}
                      >
                        {inner}
                      </button>
                    ) : (
                      <div key={lesson.id} className={rowClass}>
                        {inner}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </PageContainer>

      {/* Sticky action bar — the hero CTA's understudy. Slides in when the
          sentinel next to the hero CTA scrolls out of view, so removing the
          sidebar never strands the primary action. */}
      <AnimatePresence>
        {!ctaInView && !isInstructor && (
          <motion.div
            className="fixed inset-x-0 bottom-0 z-40 border-t border-paper-line bg-white/95 px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] backdrop-blur"
            initial={{ y: 88 }}
            animate={{ y: 0 }}
            exit={{ y: 88 }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
          >
            <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 sm:px-2">
              <div className="min-w-0 text-sm">
                <div className="truncate font-bold text-gray-900">{heroTitle}</div>
                <div className="text-xs text-gray-500">
                  {accessExpired
                    ? 'Access expired'
                    : viewState === 'prospect'
                      ? canSell
                        ? schoolPass
                          ? `Covered by ${schoolPass.schoolName}`
                          : course.price > 0
                            ? hidePrices
                              ? `${totalLessons} lesson${totalLessons === 1 ? '' : 's'}`
                              : formatPrice(course.price)
                            : 'Free'
                        : `${totalLessons} lesson${totalLessons === 1 ? '' : 's'}`
                      : viewState === 'complete'
                        ? 'Course complete'
                        : viewState === 'in-progress'
                          ? `${progress}% complete`
                          : `${totalLessons} lesson${totalLessons === 1 ? '' : 's'}`}
                </div>
              </div>
              <Button
                onClick={() => {
                  if (accessExpired) {
                    if (features.bundles && enrollment?.sourceBundleId) {
                      navigate(`/bundle/${enrollment.sourceBundleId}`);
                    } else {
                      handleAction();
                    }
                    return;
                  }
                  if (viewState === 'complete') {
                    if (course.certificateEnabled) navigate(`/certificate/${course.id}`);
                    else handleReview();
                    return;
                  }
                  handleAction();
                }}
                className="h-11 flex-shrink-0 px-5 font-bold"
                disabled={enrolling}
              >
                {accessExpired
                  ? 'Renew access'
                  : viewState === 'complete'
                    ? course.certificateEnabled
                      ? 'View certificate'
                      : 'Review course'
                    : viewState === 'in-progress'
                      ? 'Resume'
                      : viewState === 'not-started'
                        ? 'Start Learning'
                        : enrolling
                          ? 'Enrolling…'
                          : !user
                            ? 'Sign in to enrol'
                            : !canSell || schoolPass
                              ? 'Start course'
                              : course.price > 0
                                ? 'Enrol now'
                                : 'Get free access'}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};
